/**
 * Presenter Mode button — floating alongside the notifications bell.
 *
 * One tap, and the ops portal gets pitch-ready:
 *   1. POST /api/v1/ops/presenter/warm — freshens timestamps, adds live-looking
 *      telemetry pings, and ensures at least one in-transit work order.
 *   2. Pre-fetch every core sidebar page (dashboard, tracking, work orders,
 *      alerts, trips, devices, reports, users) so nothing shows a loading
 *      spinner during the demo.
 *   3. Progress modal shows each step ticking off so the presenter has
 *      visible feedback before walking on stage.
 */
import { useState } from 'react';
import { Sparkles, CheckCircle2, Loader2, X } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Every URL we hit during the pre-warm. Order matters only for the progress
// display — the actual fetches run in parallel.
const PREWARM_PAGES = [
  { key: 'dashboard',    label: 'Live Telemetry Dashboard', url: '/api/v1/ops/telemetry/dashboard?hours=24' },
  { key: 'work_orders',  label: 'Work Orders',              url: '/api/v1/work-orders?limit=100' },
  { key: 'alerts',       label: 'Alerts',                   url: '/api/v1/alerts?limit=100' },
  { key: 'trips',        label: 'Trips & Invoices',         url: '/api/v1/ops/trips/?limit=100' },
  { key: 'vans_health',  label: 'Van GPS Health',           url: '/api/v1/ops/reports/vans-health' },
  { key: 'alerts_sum',   label: 'Route Alerts Summary',     url: '/api/v1/ops/reports/route-alerts-summary' },
  { key: 'completed',    label: 'Recent CIT Runs',          url: '/api/v1/ops/reports/completed-runs?limit=20' },
  { key: 'users',        label: 'Users',                    url: '/api/v1/users/' },
];

export default function PresenterButton() {
  const { theme } = useTheme();
  const { tenant } = useTrackOticAuth();
  const isDark = theme === 'dark';
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState({}); // key -> 'pending' | 'ok' | 'err'
  const [warmResult, setWarmResult] = useState(null);

  const run = async () => {
    setRunning(true);
    setWarmResult(null);
    setStatus(Object.fromEntries(PREWARM_PAGES.map(p => [p.key, 'pending'])));
    setStatus(s => ({ ...s, warm: 'pending' }));

    // Step 1: warm the DB.
    try {
      const res = await fetch(`${API_BASE}/api/v1/ops/presenter/warm`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) throw new Error('warm failed');
      const j = await res.json();
      setWarmResult(j);
      setStatus(s => ({ ...s, warm: 'ok' }));
    } catch (e) {
      setStatus(s => ({ ...s, warm: 'err' }));
      toast.error('Warm-up failed — retry?');
      setRunning(false);
      return;
    }

    // Step 2: pre-fetch every sidebar page in parallel so the API cache is
    // populated. We swallow individual failures — one page missing shouldn't
    // block the whole warm-up.
    await Promise.all(PREWARM_PAGES.map(async (p) => {
      try {
        const r = await fetch(`${API_BASE}${p.url}`, { credentials: 'include' });
        setStatus(s => ({ ...s, [p.key]: r.ok ? 'ok' : 'err' }));
      } catch {
        setStatus(s => ({ ...s, [p.key]: 'err' }));
      }
    }));

    setRunning(false);
    toast.success(`${tenant?.name || 'Portal'} pitch-ready — go break a leg`);
  };

  const rowIcon = (state) => {
    if (state === 'ok')  return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
    if (state === 'err') return <X className="w-4 h-4 text-red-500" />;
    if (state === 'pending') return <Loader2 className="w-4 h-4 animate-spin text-teal-500" />;
    return <span className="w-4 h-4 inline-block rounded-full border border-slate-300 dark:border-slate-700" />;
  };

  return (
    <>
      <button
        type="button"
        onClick={() => { setOpen(true); if (!running) run(); }}
        title="Presenter mode — warm up for pitch"
        className={cn(
          "fixed top-3 right-16 z-40 rounded-full h-10 w-10 flex items-center justify-center shadow-md border transition",
          isDark
            ? "bg-slate-900 border-slate-700 text-teal-300 hover:bg-slate-800"
            : "bg-white border-slate-200 text-teal-600 hover:bg-slate-50",
        )}
        data-testid="ops-presenter-btn"
        aria-label="Presenter mode warm-up"
      >
        <Sparkles className="w-5 h-5" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center p-4 sm:p-10 bg-slate-900/60 backdrop-blur-sm"
          onClick={() => !running && setOpen(false)}
        >
          <div
            role="dialog"
            className={cn(
              "w-full max-w-lg rounded-2xl shadow-2xl border",
              isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-slate-200 text-slate-900"
            )}
            onClick={(e) => e.stopPropagation()}
            data-testid="ops-presenter-modal"
          >
            <div className={cn("px-6 py-4 flex items-center justify-between border-b",
              isDark ? "border-slate-800" : "border-slate-200")}>
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-teal-500" />
                <div>
                  <p className="text-sm font-semibold">Presenter Mode</p>
                  <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>
                    Pre-warming {tenant?.name || 'the portal'} for the pitch
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => !running && setOpen(false)}
                disabled={running}
                className={cn(
                  "rounded p-1",
                  isDark ? "hover:bg-slate-800 text-slate-400" : "hover:bg-slate-100 text-slate-500",
                  running && "opacity-50 cursor-not-allowed"
                )}
                aria-label="close"
                data-testid="ops-presenter-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <ul className="px-6 py-4 space-y-2 max-h-96 overflow-y-auto">
              <li className="flex items-center gap-3 text-sm">
                {rowIcon(status.warm)}
                <span>Refresh device last-seen, alerts &amp; live telemetry</span>
              </li>
              {PREWARM_PAGES.map(p => (
                <li key={p.key} className="flex items-center gap-3 text-sm">
                  {rowIcon(status[p.key])}
                  <span>Pre-fetch {p.label}</span>
                </li>
              ))}
            </ul>

            {warmResult && !running && (
              <div className={cn("px-6 py-4 text-xs border-t space-y-1",
                isDark ? "border-slate-800 text-slate-400" : "border-slate-200 text-slate-500")}>
                <p><b className={isDark ? "text-slate-200" : "text-slate-900"}>Ready:</b></p>
                <ul className="grid grid-cols-2 gap-x-6 gap-y-1">
                  <li>Devices refreshed: {warmResult.refreshed?.devices ?? '—'}</li>
                  <li>Alerts freshened: {warmResult.refreshed?.open_alerts ?? '—'}</li>
                  <li>Telemetry pings: {warmResult.refreshed?.telemetry_pings_added ?? '—'}</li>
                  <li>In-transit WOs: {warmResult.refreshed?.in_transit_work_orders ?? '—'}</li>
                  <li>Work orders total: {warmResult.page_counts?.work_orders ?? '—'}</li>
                  <li>Users total: {warmResult.page_counts?.users ?? '—'}</li>
                </ul>
              </div>
            )}

            <div className={cn("px-6 py-3 border-t flex justify-end gap-2",
              isDark ? "border-slate-800" : "border-slate-200")}>
              <button
                type="button"
                onClick={() => !running && setOpen(false)}
                disabled={running}
                className={cn(
                  "px-3 py-2 text-xs rounded-md border",
                  isDark ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-100",
                  running && "opacity-50 cursor-not-allowed"
                )}
                data-testid="ops-presenter-done"
              >
                {running ? 'Warming…' : 'Close'}
              </button>
              <button
                type="button"
                onClick={run}
                disabled={running}
                className={cn(
                  "px-3 py-2 text-xs rounded-md font-semibold text-white",
                  running ? "bg-teal-600/60 cursor-not-allowed" : "bg-teal-600 hover:bg-teal-700"
                )}
                data-testid="ops-presenter-rerun"
              >
                {running ? 'Warming…' : 'Run again'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
